import type { TQuestionRule } from 'types/rules.types';
import type { TTemplateParameters } from 'types/template/template.types';

// Appends questionId to the outline, dedup-renaming with a |N suffix when it's already present —
// the outline can legitimately contain multiple copies of the same question (e.g. several rules
// jumping to the same target), each needing a distinct list entry.
export function appendToOutlineWithDedupRename(questionList: string[], questionId: string): string[] {
  const count = questionList.filter((id) => id.startsWith(questionId)).length;
  const newId = count > 0 ? `${questionId}|${count + 1}` : questionId;
  return [...questionList, newId];
}

// Undoes one appendToOutlineWithDedupRename call — removes the last outline entry matching
// questionId (exact id or a |N-suffixed copy), or does nothing if there isn't one.
export function removeOneFromOutlineByPrefix(questionList: string[], questionId: string): string[] {
  const index = questionList.findLastIndex((id) => id.startsWith(questionId));
  if (index === -1) return questionList;
  const next = [...questionList];
  next.splice(index, 1);
  return next;
}

// Sets parameters[nodeId].rules to `rules` outright, creating the parameters entry if it doesn't
// exist yet, without touching any other keys already there (e.g. `options`) — the write-side
// counterpart to getNodeRules' "an override always wins, even an empty one" read contract.
export function setRulesOnNodeParams(parameters: TTemplateParameters, nodeId: string, rules: TQuestionRule[]): void {
  parameters[nodeId] = { ...parameters[nodeId], rules };
}

// Seeds parameters[nodeId].rules from `currentRules` (the question's own default rules, when
// there's no override yet — see getNodeRules) only if it isn't already there, then returns
// whatever rules array is now in place. An existing override is left completely untouched, even
// if the caller's `currentRules` snapshot happens to differ from it — the override is always the
// source of truth once it exists. This is what lets every "edit one existing rule" reducer safely
// index into parameters[nodeId].rules[ruleIdx] without assuming an override was already created
// before it runs.
//
// `currentRules` is deep-cloned before seeding: it's typically the same rule objects cached in
// the Library slice, which Redux/Immer auto-freezes (deeply) once the reducer that populated it
// finishes. Seeding with those references as-is would mean a later push onto a seeded rule's own
// conditions/actions array (customQuestionRuleConditionPlus/ActionPlus) throws "object is not
// extensible" instead of mutating a fresh copy.
export function ensureRulesOverride(
  parameters: TTemplateParameters,
  nodeId: string,
  currentRules: TQuestionRule[],
): TQuestionRule[] {
  const params = parameters[nodeId];
  if (!params || !Object.prototype.hasOwnProperty.call(params, 'rules')) {
    const clonedRules = currentRules.map((rule) => JSON.parse(JSON.stringify(rule)));
    setRulesOnNodeParams(parameters, nodeId, clonedRules);
  }
  return parameters[nodeId].rules;
}

// Adds `rule` onto the question's currently-effective rules (an existing override, or
// `currentRules` — the question's own defaults — when there's no override yet), creating the
// override if needed, without touching any other keys already there (e.g. `options`). Building on
// `currentRules` rather than starting from just `[rule]` is what stops adding one rule from making
// every other rule the question already has (e.g. one just set via the early-reject checkbox)
// disappear from the override until the next server round trip corrects it.
export function pushRuleToNodeParams(
  parameters: TTemplateParameters,
  nodeId: string,
  rule: TQuestionRule,
  currentRules: TQuestionRule[],
): void {
  const rules = ensureRulesOverride(parameters, nodeId, currentRules);
  rules.push(rule);
}

// Flattens the question ids a set of rules' actions jump to (a JUMP_TO action's `value` holds
// either raw ids or `{id}` objects) — used to find outline entries that only exist because of
// rules that are about to be deleted.
export function collectRuleJumpTargetIds(rules: TQuestionRule[]): string[] {
  return rules.flatMap(
    (rule) => rule.actions?.flatMap((action) => action.value.map((v) => (typeof v === 'object' ? v.id : v))) ?? [],
  );
}

export const updateNonFollowupScoringCategory = (categories: any, category: any, nodeId: string, weight: number) => {
  if (Object.prototype.hasOwnProperty.call(categories, category)) {
    if (categories[category].required_questions.indexOf(nodeId) === -1) {
      categories[category].required_questions.push(nodeId);
    }
  } else {
    categories.list.push(category);
    categories[category] = {
      name: '',
      required_questions: [nodeId],
      weight: weight,
      optional: [],
      optional_groups: {},
    };
  }
};

export const updateFollowupScoringCategory = (
  categories: any,
  category: any,
  nodeId: string,
  weight: number,
  parentIds: string,
) => {
  if (Object.prototype.hasOwnProperty.call(categories, category)) {
    categories[category].optional.push(nodeId);
    const parents = parentIds.split('#');
    let group = categories[category].optional_groups;
    parents.map((parent, idx: number) => {
      if (parent in group) {
        if (parents.length - 1 === idx) {
          group[parent].questions.push(nodeId);
        }
      } else {
        group[parent] = {
          questions: [nodeId],
          children: {},
        };
      }
      group = group[parent].children;
    });
  } else {
    categories.list.push(category);
    categories[category] = {
      name: '',
      required_questions: [],
      weight: weight,
      optional: [nodeId],
      optional_groups: {
        [parentIds]: {
          questions: [nodeId],
          children: {},
        },
      },
    };
  }
};
